import { useEffect } from 'react';

import { useQuery } from '@apollo/client';
import { date } from '@crehana/utils';

import GET_LATEST_THREE_MONTH_ACTIVITY from '../graphql/GetLatestThreeMonthUserActivity.v5.graphql';
import type {
  GetLatestThreeMonthUserActivity,
  GetLatestThreeMonthUserActivityVariables as IQueryVariables,
} from '../graphql/types/GetLatestThreeMonthUserActivity';

interface useLatestThreeMonthUserActivityProps
  extends Omit<IQueryVariables, 'answers'> {
  selectedTags?: IQueryVariables['answers'][];
}

const useLatestThreeMonthUserActivity = ({
  attributeId,
  organizationId,
  selectedTags,
}: useLatestThreeMonthUserActivityProps) => {
  const shouldSkip =
    !organizationId ||
    (!!attributeId && (!selectedTags || selectedTags.length === 0));

  const { data, loading, error, refetch } = useQuery<
    GetLatestThreeMonthUserActivity,
    IQueryVariables
  >(GET_LATEST_THREE_MONTH_ACTIVITY, {
    variables: {
      organizationId,
      attributeId,
      answers: selectedTags?.[0],
    },
    context: { clientName: 'v5' },
    skip: shouldSkip,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  useEffect(() => {
    if (attributeId) {
      refetch({
        attributeId,
        organizationId,
      });
    }
  }, [attributeId]);

  const threeMonthsData =
    data?.organization_monthly_activity.map(stats => ({
      date: date.getMonthName(Number(stats.month) - 1),
      quantity: Math.ceil((stats.played_seconds || 0) / 3600),
    })) || [];

  return {
    threeMonthsData:
      threeMonthsData.length === 0
        ? [
            {
              date: date.getMonthName(new Date().getMonth() - 2),
              quantity: 0,
            },
            {
              date: date.getMonthName(new Date().getMonth() - 1),
              quantity: 0,
            },
            {
              date: date.getMonthName(),
              quantity: 0,
            },
          ]
        : threeMonthsData,
    loading,
    error,
  };
};

export default useLatestThreeMonthUserActivity;
